import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { onAppearance, savedAppearance } from "./appearance.ts";
import type { Theme } from "./map/style.ts";

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const list = matchMedia(query);
    const update = () => setMatches(list.matches);
    list.addEventListener("change", update);
    update();
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}

// Daylight or Night, as the device is set.
// The theme the page shows: my choice on this device, else the device's.
export function useTheme(): Theme {
  const device = useMedia("(prefers-color-scheme: dark)") ? "dark" : "light";
  const [choice, setChoice] = useState(savedAppearance);
  useEffect(() => onAppearance(() => setChoice(savedAppearance())), []);
  return choice === "system" ? device : choice;
}

export type Load<T> =
  | { state: "loading" }
  | { state: "ready"; data: T }
  // `retrying`: loading again (Try again, back online), the failure still showing until the answer comes.
  | { state: "failed"; message: string; error: Error; retrying?: boolean };

// Loads when the deps change. Loading again keeps showing what was there (an answer, or why there was none)
// until the new answer comes, so Try again never blinks the page.
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): Load<T> {
  const [result, setResult] = useState<Load<T>>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setResult((last) =>
      last.state === "ready" ? last : last.state === "failed" ? { ...last, retrying: true } : { state: "loading" },
    );
    load().then(
      (data) => live && setResult({ state: "ready", data }),
      (error: Error) => live && setResult({ state: "failed", message: error.message, error }),
    );
    return () => {
      live = false;
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: the caller names what the load depends on
  }, deps);
  return result;
}

// How tall an element is (0 while there's none), as it changes: a ref to put on it, and its height.
// Measured as the element is attached, before the page paints, so what depends on it (the map's
// opening frame) has it from the start.
export function useHeight(): [(el: HTMLElement | null) => void, number] {
  const [height, setHeight] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) {
      setHeight(0);
      return;
    }
    setHeight(el.offsetHeight);
    observer.current = new ResizeObserver(() => setHeight(el.offsetHeight));
    observer.current.observe(el);
  }, []);
  return [ref, height];
}

// A text box as tall as its words (DESIGN.md, Views, "A text box shows all it holds"): opened over what's written it
// shows all of it, and it grows as words are typed and shrinks as they're deleted, between its CSS min-height (an
// empty one's few lines) and its max-height (about half the screen, app.css), past which it scrolls inside itself.
// Sized before the page paints, so it never opens cut through a line, and again when its width changes (a phone
// turned, a dialog shown) or the page's fonts come in. A ref to put on the textarea, given its value.
// (CSS's `field-sizing: content` does this alone, but only from 2026's browsers: an older iPhone's Safari has none.)
export function useGrow(value: string): (el: HTMLTextAreaElement | null) => void {
  const box = useRef<HTMLTextAreaElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLTextAreaElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    box.current = el;
    if (!el) return;
    let width = el.offsetWidth;
    observer.current = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      toWords(el);
    });
    observer.current.observe(el);
    if (document.fonts.status !== "loaded") void document.fonts.ready.then(() => box.current === el && toWords(el));
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: sized again whenever its words change
  useLayoutEffect(() => {
    if (box.current) toWords(box.current);
  }, [value]);
  return ref;
}

// Measured at its least (its rows, or its min-height), with no scrollbar to narrow its lines, then set as tall as
// what's in it. Whatever scrolls around it is put back where it was: shrunk to measure, the box would pull a dialog
// scrolled to its end up short.
function toWords(el: HTMLTextAreaElement) {
  const scrolled: [Element, number][] = [];
  for (let a = el.parentElement; a; a = a.parentElement) if (a.scrollTop > 0) scrolled.push([a, a.scrollTop]);
  el.style.overflowY = "hidden";
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  el.style.overflowY = "";
  for (const [a, top] of scrolled) a.scrollTop = top;
}
